import { Data, Datakatalog } from '@/bootstrap/data';
import { MapLayerType } from '@/components/map/map-utils/WebMapTileServices';
import {
    Kontraktsomrade,
    Land,
    Omrade,
    Riksvegrute,
    Vegsystemreferanse,
} from '@/domain/omrader';
import { Polygon } from '@/domain/omrader/Polygon';
import { MapPoint } from '@/domain/kart/MapPoint';
import { Node } from '@/domain/vegnett/Node';
import { Egenskapstype, EgenskapTillattVerdi } from '@/domain/vegobjekter/Egenskapstype';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { WKTObject } from '@/domain/WKTObject';
import { Strekning, Vegsystem } from '@/domain/omrader/Vegsystemreferanse';
import { Integration } from '@/middleware/integration';
import {
    Egenskapsfilter,
    Operator,
    OperatorAsString,
    VegnettQueries,
} from '@/middleware/queries';
import { Stedsnavn } from '@/server/responses';
import {
    AdvancedFilterHash,
    advancedFilterFromHash,
    migrateLegacyFiltersToAdvancedFilter,
} from '@/domain/advancedFilter';
import {
    MapState,
    OmraderState,
    RootState,
    SearchState,
    ValgtState,
    VegnettRetning,
    VegnettState,
    VegobjekterState,
    VegobjekttypeState,
    VegsystemreferanseLookup,
} from '@/state';
import { ColorHandler } from '@/utils/ColorHelper';
import { resolveInitialVegobjekttypeColor } from '@/utils/ColorPreferences';
import { createVegrefParser } from '@/utils/VegrefParser';
import { parseISO } from 'date-fns';
import { parse } from 'jsurl2';
import qs from 'qs';
import * as uuidgen from 'uuid';
import { SplashState } from '../SplashState';
import {
    filterUnique,
    uniqueByEnumid,
    uniqueByKortform,
    uniqueByNavn,
    uniqueByNummer,
} from './hashHelper';

const qsOptions: qs.IParseOptions<qs.BooleanOptional> = { arrayLimit: 1000 };

type HashEncoding = 'jsurl' | 'base64' | 'qs';

export function detectEncoding(input: string): HashEncoding {
    const jsurlPattern = /~/;
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;

    if (jsurlPattern.test(input) && !input.includes('&') && !input.includes('=')) {
        return 'jsurl';
    } else if (base64Pattern.test(input) && input.length % 4 === 0) {
        return 'base64';
    } else {
        return 'qs';
    }
}

export function parseHashWithFallback<T>(input: string): T {
    const encoding = detectEncoding(input);
    if (encoding === 'jsurl') {
        return parse(input) as T;
    } else if (encoding === 'base64') {
        return qs.parse(atob(input), qsOptions) as T;
    } else {
        return qs.parse(input, qsOptions) as T;
    }
}

export function hashToState(
    integration: Integration,
    previousState: RootState,
    incomingUrlState: string
) {
    const urlState = `/${incomingUrlState.replace(/typeId/g, 'type_id')}`;
    try {
        return previousState
            .withMap(
                mapState(urlState, previousState.preferences.mapLayer).withPoints(
                    markorerState(urlState)
                )
            )
            .withVegnett(roadNetState(urlState))
            .withOmrader(parseOmraderState(urlState, integration.data))
            .withVegobjekter(
                hvaState(urlState, integration.data, integration.colorHandler)
            )
            .withValgt(valgtObjekt(urlState))
            .withOverlay(
                previousState.overlayState
                    .withVegsystemreferanseLookup(
                        vegsystemreferanseSok(urlState) as VegsystemreferanseLookup
                    )
                    .withPlacenameMarker(placenameMarkerState(urlState))
            )
            .withSplash(splash(urlState))
            .withSearch(searchState(urlState));
    } catch (e) {
        console.error('Failed to parse state from URL.', e);
        return previousState;
    }
}

const mapStatePattern = /\/@:?([^/]+)?/;
const narStatePattern = /når:([^/]+)?/;
const hvorStatePattern = /hvor:([^/]+)?/;
const roadNetPattern = /\/vegnett:([^/]+)?/;
const hvaStatePattern = /\/hva:([^#\/]+)?/;
const vegobjektPattern = /\/vegobjekt:?([^/]+)?/;
const valgtObjektPattern = /\/valgt:?([^/]+)?/;
const nodePattern = /\/noder:?([^/]+)?/;
const linkQueryPattern = /\/lenker:?([^/]+)?/;
const veglenkePattern = /\/veglenke:?([^/]+)?/;
const vegsystemreferanseCoordinatePattern = /\/vegsystemreferanse:([^/:]+):([^/]+)/;
const vegsystemreferansePatternDot =
    /\/vegsystemreferanse:(?<kommune>\d{4})?(?<ref>[\d.]+)/;
const vegsystemreferansePattern = /\/vegsystemreferanse:(?<kommune>\d{4})?(?<ref>\w+)/;
const splashPattern = /\/splash:(\w+)/;
const markorePattern = /\/markorer:?([^/]+)?/;
const stedsnavnPattern = /\/stedsnavn:?([^/]+)?/;

function mapState(urlState: string, mapLayerFromPreferences: MapLayerType): MapState {
    const match = urlState.match(mapStatePattern)?.[1];
    if (match) {
        const [east, north, zoom] = match.split(',').map(n => parseInt(n, 10));
        if (!east || !north) {
            console.warn('Invalid map center point', [east, north]);
            return new MapState(mapLayerFromPreferences);
        }
        return new MapState(mapLayerFromPreferences, [east, north], zoom || 3);
    }
    return new MapState(mapLayerFromPreferences);
}

function searchState(urlState: string): SearchState {
    const match = urlState.match(narStatePattern)?.[1];
    const state = new SearchState();
    if (match) {
        const dateStr = match.replace('date=', '');
        const parsedDate = parseISO(dateStr);
        if (isNaN(parsedDate.getTime())) {
            console.error('Invalid date format:', dateStr);
            return state;
        }
        return state.withDropdownState(
            state.searchDropdownState.withTimestamp(parsedDate)
        );
    }
    return state;
}

type HiddenAreaIds = string | number | (string | number)[];

type HvorUrlState = {
    land?: string[];
    fylke?: number[];
    kommune?: number[];
    kontraktsomrade?: string[];
    riksvegrute?: string[];
    vegsystemreferanse?: string[];
    polygon?: string;
    skjult?: {
        land?: HiddenAreaIds;
        fylke?: HiddenAreaIds;
        kommune?: HiddenAreaIds;
        kontraktsomrade?: HiddenAreaIds;
        riksvegrute?: HiddenAreaIds;
        vegsystemreferanse?: HiddenAreaIds;
        polygon?: string | boolean;
    };
};

const sameLand = (a: Land, b: string | number) =>
    a.navn.toLowerCase() === `${b}`.toLowerCase();
const sameNummer = (a: { nummer: number }, b: string | number) => a.nummer === Number(b);
const sameKontraktsomrade = (a: Kontraktsomrade, b: string | number) =>
    a.navn === `${b}`.replace(/[,]/g, '');
const sameRiksvegrute = (a: Riksvegrute, b: string | number) =>
    a.enumid === Number(b) || `${a.navn} ${a.enumid}` === `${b}`;
const sameVegsystemreferanse = (a: Vegsystemreferanse, b: string | number) =>
    a.toParams() === `${b}`;

function hideAreas<T extends Omrade, K>(
    areas: T[],
    hiddenIds: HiddenAreaIds | undefined,
    comparator: (area: T, id: K) => boolean
): T[] {
    if (hiddenIds === undefined) return areas;
    const ids = (Array.isArray(hiddenIds) ? hiddenIds : [hiddenIds]) as K[];
    return areas.map(area =>
        ids.some(id => comparator(area, id)) ? (area.withVisible(false) as T) : area
    );
}

function isPolygonHidden(value: string | boolean | undefined): boolean {
    if (value === undefined || value === false || value === 'false') return false;
    if (value === true || value === 'true') return true;
    throw new Error('Invalid polygon visibility in URL');
}

function parseOmraderState(urlState: string, data: Data): OmraderState {
    const match = urlState.match(hvorStatePattern)?.[1];
    if (!match) return new OmraderState();

    const hvor = parseHashWithFallback<HvorUrlState>(decodeURIComponent(match));
    const skjult = hvor.skjult ?? {};
    const polygonHidden = isPolygonHidden(skjult.polygon);
    const { kommuner, fylker, riksvegruter, kontraktsomrader, land, datakatalog } = data;
    const vegobjekttypeVegsystem = datakatalog.getTypeById(915);
    const vegsysrefParser = createVegrefParser(vegobjekttypeVegsystem);

    return new OmraderState(
        hideAreas(
            filterUnique(hvor.land || [], land, sameLand, uniqueByNavn),
            skjult.land,
            sameLand
        ),
        hideAreas(
            filterUnique(hvor.fylke || [], fylker, sameNummer, uniqueByNummer),
            skjult.fylke,
            sameNummer
        ),
        hideAreas(
            filterUnique(hvor.kommune || [], kommuner, sameNummer, uniqueByNummer),
            skjult.kommune,
            sameNummer
        ),
        hideAreas(
            filterUnique(
                hvor.kontraktsomrade || [],
                kontraktsomrader,
                sameKontraktsomrade,
                uniqueByNavn
            ),
            skjult.kontraktsomrade,
            sameKontraktsomrade
        ),
        hideAreas(
            filterUnique(
                hvor.riksvegrute || [],
                riksvegruter,
                sameRiksvegrute,
                uniqueByEnumid
            ),
            skjult.riksvegrute,
            sameRiksvegrute
        ),
        hideAreas(
            uniqueByKortform(
                (hvor.vegsystemreferanse || [])
                    .map(vsr => vegsysrefParser(vsr))
                    .filter(
                        (vegsystemreferanse): vegsystemreferanse is Vegsystemreferanse =>
                            vegsystemreferanse !== null
                    )
            ),
            skjult.vegsystemreferanse,
            sameVegsystemreferanse
        ),
        hvor.polygon
            ? new Polygon(new WKTObject(5973, hvor.polygon)).withVisible(!polygonHidden)
            : null
    );
}

function parseJsurlRoadNet(urlState: string): VegnettState {
    const match = urlState.match(roadNetPattern)?.[1]?.split('+');
    if (!match)
        return new VegnettState().withVisible(false).withFilter(new VegnettQueries());

    const parsedDirection = parseHashWithFallback<VegnettRetning>(match[0]);
    const parsedFilter = Object.assign(
        new VegnettQueries(),
        parseHashWithFallback(match[1])
    ).fromHash();
    return new VegnettState()
        .withVisible(true)
        .withDirection(parsedDirection)
        .withFilter(parsedFilter);
}

function parseQsRoadNet(urlState: string): VegnettState {
    const match = urlState.match(roadNetPattern)?.[1];
    if (match) {
        const parsedObject = parseHashWithFallback<{
            filter?: Record<string, unknown>;
            direction?: VegnettRetning;
            visible?: boolean;
        }>(match);
        const parsedFilter = Object.assign(
            new VegnettQueries(),
            parsedObject.filter
        ).fromHash();

        return new VegnettState()
            .withVisible(!!parsedObject.visible)
            .withDirection(parsedObject.direction as VegnettRetning)
            .withFilter(parsedFilter);
    }
    return new VegnettState()
        .withVisible(false)
        .withFilter(new VegnettQueries())
        .withDirection(VegnettRetning.METRERING);
}

export function roadNetState(urlState: string): VegnettState {
    const encoding = detectEncoding(urlState);
    if (encoding === 'jsurl') {
        return parseJsurlRoadNet(urlState);
    } else {
        return parseQsRoadNet(urlState);
    }
}

type HvaUrlState = {
    hva: {
        category: Category;
        absoluteIntervals: string | boolean;
        id: string | number;
        intervals?: (string | number)[];
        filter?: EgenskapsFilterUrl[];
        advanced_filter?: AdvancedFilterHash;
    }[];
};
type Category = { type: string; id: string | number };
type EgenskapsFilterUrl = {
    type_id: string | number;
    operator: OperatorAsString;
    verdi: string | number | null | (string | number | null)[];
};

function hvaState(
    urlState: string,
    data: Data,
    colorHandler: ColorHandler
): VegobjekterState {
    const match = urlState.match(hvaStatePattern)?.[1];
    if (!match) return new VegobjekterState();

    const parsedHash = parseHashWithFallback<HvaUrlState | HvaUrlState['hva']>(match);
    const types = Array.isArray(parsedHash) ? parsedHash : parsedHash.hva || [];
    const { datakatalog } = data;
    return new VegobjekterState(
        types.map(type => {
            const typeId = parseInt(String(type.id), 10);
            const legacyFilters = (type.filter || [])
                .map(filter =>
                    mapEgenskapsfilter(filter, datakatalog.getTypeById(typeId))
                )
                .filter((filter): filter is Egenskapsfilter => filter !== null);
            const advancedFilter = type.advanced_filter
                ? advancedFilterFromHash(type.advanced_filter, datakatalog, typeId)
                : migrateLegacyFiltersToAdvancedFilter(legacyFilters);
            return new VegobjekttypeState(
                typeId,
                resolveInitialVegobjekttypeColor(typeId, colorHandler),
                type.category ? parseCategory(typeId, type.category, datakatalog) : null,
                (type.intervals || []).map(interval =>
                    Number.parseFloat(String(interval))
                ),
                [],
                legacyFilters
            )
                .withAdvancedFilters(advancedFilter, advancedFilter)
                .withAbsoluteIntervals(
                    type.absoluteIntervals === true || type.absoluteIntervals === 'true'
                );
        })
    );
}

function parseCategory(
    typeId: number,
    category: Category,
    datakatalog: Datakatalog
): Egenskapstype | null {
    return datakatalog
        .getTypeById(typeId)
        .egenskapstypeById(Number.parseInt(String(category.id)));
}

type alphanum = string | number;
type SelectedVegobjektUrl = { id: string | number; type: string | number };
type ValgtUrlState = {
    vegobjekter?: SelectedVegobjektUrl[];
    clusterObjekter?: SelectedVegobjektUrl[];
};
type PlacenameMarkerUrlState = {
    ssrID?: alphanum | alphanum[];
    placeNameType?: string | string[];
    category?: string | string[];
    name?: string | string[];
    kommune?: string | string[];
    fylke?: string | string[];
    xCoord?: alphanum | alphanum[];
    yCoord?: alphanum | alphanum[];
};

function getEgenskapsverdi(
    verdi: alphanum | null | (alphanum | null)[],
    egenskapstype: Egenskapstype | null
): EgenskapTillattVerdi | number | string | null {
    if (Array.isArray(verdi)) {
        verdi = verdi[0];
    }
    if (
        verdi === null ||
        typeof verdi === 'object' ||
        egenskapstype === null ||
        verdi === ''
    ) {
        return null;
    }
    if (egenskapstype.erEnum()) {
        return (
            egenskapstype.verdiById(
                typeof verdi === 'number' ? verdi : parseInt(verdi)
            ) ?? null
        );
    }
    if (egenskapstype.erHeltall()) {
        return typeof verdi === 'number' ? verdi : parseInt(verdi);
    }
    if (egenskapstype.erFlyttall()) {
        return typeof verdi === 'number' ? verdi : parseFloat(verdi.replace(',', '.'));
    }
    if (egenskapstype.erTekst()) {
        return verdi;
    }
    if (egenskapstype.erDato()) {
        return verdi;
    }
    console.warn('No mapping for egenskapstype ' + egenskapstype.egenskapstype);
    return null;
}

function mapEgenskapsfilter(
    filter: EgenskapsFilterUrl,
    vegobjekttype: Vegobjekttype
): Egenskapsfilter | null {
    const egenskapstypeId = parseInt(String(filter.type_id), 10);
    const egenskapstype = vegobjekttype.egenskapstypeById(egenskapstypeId);
    if (!egenskapstype) {
        return null;
    }

    const verdi = getEgenskapsverdi(filter.verdi, egenskapstype);
    const operator = Operator.from(filter.operator, verdi);
    return new Egenskapsfilter(egenskapstype, operator, verdi ?? undefined);
}

export function valgtObjekt(urlState: string): ValgtState {
    let state = ValgtState.emptySelection();
    const linkMatch = urlState.match(linkQueryPattern)?.[1];
    const vegobjektMatch = urlState.match(vegobjektPattern)?.[1];
    const valgtObjektMatch = urlState.match(valgtObjektPattern)?.[1];
    const veglenkeMatch = urlState.match(veglenkePattern)?.[1];
    const nodeMatch = urlState.match(nodePattern)?.[1];

    if (linkMatch) {
        const queryStrings = parseHashWithFallback<{ links: string[] }>(linkMatch);
        state = state
            .withLinkQueries(
                Object.fromEntries((queryStrings.links || []).map(link => [link, []]))
            )
            .withFetching(true);
    }

    if (vegobjektMatch) {
        const { id, type } = getIdAndTypeFromMatch(vegobjektMatch);
        state = state.withVegobjekter([Vegobjekt.dummy(id, type)]).withFetching(true);
    }

    if (valgtObjektMatch) {
        const structuredValgtState = parseStructuredValgtState(valgtObjektMatch);

        if (structuredValgtState) {
            state = state
                .withVegobjekter(toDummyVegobjekter(structuredValgtState.vegobjekter))
                .withClusterObjekter(
                    toDummyVegobjekter(structuredValgtState.clusterObjekter)
                )
                .withFetching(true);
        } else {
            const split = valgtObjektMatch.split(':');
            if (split.length === 1) {
                const { id } = getIdAndTypeFromMatch(valgtObjektMatch);
                state = state
                    .withVegobjekter([Vegobjekt.dummy(id, null)])
                    .withFetching(true);
            } else if (split.length === 2) {
                const { id, type } = getIdAndTypeFromMatch(valgtObjektMatch);
                state = state
                    .withVegobjekter([Vegobjekt.dummy(id, type)])
                    .withFetching(true);
            } else if (split.length === 3) {
                const { id, lenke, segment } =
                    getIdLenkeAndSegmentFromMatch(valgtObjektMatch);
                state = state
                    .withVegnettlenkeReferanse({
                        veglenkesekvensid: id,
                        veglenkenummer: lenke,
                        segmentnummer: segment,
                    })
                    .withFetching(true);
            }
        }
    } else if (veglenkeMatch) {
        const splitLength = veglenkeMatch.split(':').length;
        if (splitLength === 2) {
            const { id, type } = getIdAndTypeFromMatch(veglenkeMatch);
            state = state
                .withVegnettlenkeReferanse({
                    veglenkesekvensid: id,
                    veglenkenummer: type,
                })
                .withFetching(true);
        } else {
            const { id, lenke, segment } = getIdLenkeAndSegmentFromMatch(veglenkeMatch);
            state = state
                .withVegnettlenkeReferanse({
                    veglenkesekvensid: id,
                    veglenkenummer: lenke,
                    segmentnummer: segment,
                })
                .withFetching(true);
        }
    }

    if (nodeMatch) {
        const parsedNodes = parseHashWithFallback<{ nodes?: alphanum[] } | alphanum[]>(
            nodeMatch
        );
        const nodeIds = Array.isArray(parsedNodes)
            ? parsedNodes
            : parsedNodes.nodes || [];
        state = state
            .withNodes(
                nodeIds.map(
                    nodeId =>
                        new Node(
                            parseInt(String(nodeId), 10),
                            null as unknown as WKTObject,
                            '',
                            '',
                            []
                        )
                )
            )
            .withFetching(true);
    }

    return state;
}

function parseStructuredValgtState(match: string): ValgtUrlState | null {
    if (!match.includes('=') && !match.includes('~')) {
        return null;
    }

    const parsed = parseHashWithFallback<ValgtUrlState>(match);
    return hasStructuredVegobjekter(parsed?.vegobjekter) ||
        hasStructuredVegobjekter(parsed?.clusterObjekter)
        ? parsed
        : null;
}

function hasStructuredVegobjekter(vegobjekter?: SelectedVegobjektUrl[]) {
    return Array.isArray(vegobjekter) && vegobjekter.length > 0;
}

function toDummyVegobjekter(vegobjekter: SelectedVegobjektUrl[] = []) {
    return vegobjekter.map(vegobjekt =>
        Vegobjekt.dummy(Number(vegobjekt.id), Number(vegobjekt.type))
    );
}

function getIdAndTypeFromMatch(match: string) {
    const [id, type] = match.split(':').map(Number);
    return { id, type };
}

function getIdLenkeAndSegmentFromMatch(match: string) {
    const [id, lenke, segment] = match.split(':').map(Number);
    return { id, lenke, segment };
}

function firstValue<T>(value?: T | T[]): T | undefined {
    return Array.isArray(value) ? value[0] : value;
}

function placenameMarkerState(urlState: string): Stedsnavn | null {
    const match = urlState.match(stedsnavnPattern)?.[1];
    if (!match) return null;

    const parsed = parseHashWithFallback<PlacenameMarkerUrlState>(match);
    const name = firstValue(parsed?.name);
    const xCoord = firstValue(parsed?.xCoord);
    const yCoord = firstValue(parsed?.yCoord);

    if (!name || xCoord == null || yCoord == null) {
        return null;
    }

    const ssrID = firstValue(parsed?.ssrID);

    return new Stedsnavn(
        ssrID == null ? 0 : Number(ssrID),
        String(firstValue(parsed?.placeNameType) || ''),
        String(firstValue(parsed?.category) || ''),
        String(name),
        String(firstValue(parsed?.kommune) || ''),
        String(firstValue(parsed?.fylke) || ''),
        String(xCoord),
        String(yCoord)
    );
}

export function vegsystemreferanseSok(urlState: string): VegsystemreferanseLookup | null {
    const coordMatch = urlState.match(vegsystemreferanseCoordinatePattern);
    const refMatch =
        urlState.match(vegsystemreferansePattern) ||
        urlState.match(vegsystemreferansePatternDot);

    if (coordMatch) {
        const [x, y] = [parseFloat(coordMatch[1]), parseFloat(coordMatch[2])];
        if (!isNaN(x) && !isNaN(y)) {
            return new VegsystemreferanseLookup(x, y);
        }
    } else if (refMatch) {
        const groups = refMatch.groups;
        if (!groups?.ref) return null;
        const vegsystemreferanse = new Vegsystemreferanse(
            null as unknown as Vegsystem,
            null as unknown as Strekning,
            null,
            null,
            null,
            groups.ref,
            null,
            null
        );
        const kommune = groups.kommune ? parseInt(groups.kommune) : null;
        return new VegsystemreferanseLookup(
            0,
            0,
            kommune === null
                ? vegsystemreferanse
                : vegsystemreferanse.withKommune(kommune)
        ).override({ kommune });
    }
    return null;
}

function markorerState(urlState: string): MapPoint[] {
    const match = urlState.match(markorePattern)?.[1];
    if (!match) return [];
    return match
        .split(';')
        .filter(Boolean)
        .map(segment => segment.split(','))
        .filter(([east, north]) => east != null && north != null)
        .map(([east, north]) => ({
            id: uuidgen.v4(),
            geometry: [Number(east), Number(north)] as [number, number],
        }))
        .filter(
            point => !Number.isNaN(point.geometry[0]) && !Number.isNaN(point.geometry[1])
        );
}

function splash(urlState: string): SplashState {
    const match = urlState.match(splashPattern)?.[1];
    if (!match) return SplashState.NONE;

    const splashStates: Record<string, SplashState> = {
        NONE: SplashState.NONE,
        MAIN: SplashState.MAIN,
        POLICY: SplashState.POLICY,
        CHANGELOG: SplashState.CHANGELOG,
        PREFERENCES: SplashState.PREFERENCES,
        NOTIFICATION: SplashState.NOTIFICATION,
    };
    return splashStates[match.toUpperCase()] ?? SplashState.NONE;
}
